// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useCallback, useEffect, useRef } from 'react';
import type { ThemePreference } from './useResolvedTheme';
import { agentConfig } from '../agent-config';

export interface UserPreferences {
  rememberOrganisation: boolean;
  theme: ThemePreference;
  /** BCP 47 locale code, or empty string for system default (cascade resolution). */
  language: string;
}

const DEFAULTS: UserPreferences = {
  rememberOrganisation: true,
  theme: 'system',
  language: '',
};

const STORAGE_KEY_PREFIX = `${agentConfig.agentSlug}-agent-preferences`;

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}-${userId}`;
}

function load(userId: string | undefined): UserPreferences {
  if (!userId) return { ...DEFAULTS };
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<UserPreferences>;
      return { ...DEFAULTS, ...parsed };
    }
  } catch { /* ignore corrupt data */ }
  return { ...DEFAULTS };
}

function save(userId: string | undefined, prefs: UserPreferences): void {
  if (!userId) return; // never persist without a user identity
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch { /* ignore quota errors */ }
}

export function useUserPreferences(userId: string | undefined) {
  const [preferences, setPreferences] = useState<UserPreferences>(() => load(userId));
  const prevUserId = useRef(userId);

  // Reload preferences when userId becomes available (MSAL async login)
  useEffect(() => {
    if (userId && userId !== prevUserId.current) {
      setPreferences(load(userId));
    }
    prevUserId.current = userId;
  }, [userId]);

  const updatePreference = useCallback(<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ) => {
    setPreferences(prev => {
      const next = { ...prev, [key]: value };
      save(userId, next);
      return next;
    });
  }, [userId]);

  return { preferences, updatePreference };
}
